import { Button } from '@shared/components/common/Button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@shared/components/common/Form';
import { PasswordInput } from '@shared/components/common/Input';
import { useStore } from '@shared/hooks/useStore';
import yup from '@shared/services/yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { ArrowLeft } from 'lucide-react';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';
import SuccessIcon from '@shared/assets/images/success_icon.svg';
import { NotFoundPage } from '..';
import { PostConfirmPasswordRequest } from '@shared/types/http-payload/auth';
import { ROUTES } from '@shared/configs/constants';
import { PASSWORD_REGEX } from '@shared/configs/regex';

export default observer(function ConfirmPasswordPage() {
    // hook
    const [params] = useSearchParams();
    const uuid = params?.get('uuid') || null;
    const isForgetPassword = params.get('isForgetPassword');

    // store
    const {
        authStore: { confirmPassword, getCheckUuidConfirmPassword }
    } = useStore();

    // validate schema
    const validateSchema = yup.object().shape({
        password: yup.string().required('error.required').matches(
            PASSWORD_REGEX,
            'アルファベットの小文字・大文字・数字を含む8～20文字でご入力ください'
        ),
        passwordConfirm: yup.string().oneOf([yup.ref('password')], '上記パスワードと一致しないため再入力をお願いします。').required('必須項目をご入力ください')
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: {
            password: '',
            passwordConfirm: ''
        }
    });

    // const
    const [isCheckUuidOk, setIsCheckUuidOk] = useState<boolean>(true);
    const [isConfirmOk, setIsConfirmOk] = useState<boolean>(false);
    const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
    const [showConfirmNewPassword, setShowConfirmNewPassword] = useState<boolean>(false);

    // lifecycle
    useEffect(() => {
        onCheckUuid();
    }, []);

    // function
    const onCheckUuid = async () => {
        const res = await flowResult(getCheckUuidConfirmPassword({ uuid }));
        if (res) {
            setIsCheckUuidOk(true);
        } else {
            setIsCheckUuidOk(false);
        }
    };

    const onSubmit = async (dataForm: FormData) => {
        const body: PostConfirmPasswordRequest = {
            password: dataForm?.password || '',
            uuid: uuid || ''
        };

        const res = await flowResult(confirmPassword(body));
        if (res) {
            setIsConfirmOk(true);
        }
    };

    if (!uuid || !isCheckUuidOk) {
        return <NotFoundPage />;
    }

    return (
        <div className='sm:bg-background-2 bg-white my-[40px] flex justify-center items-center lg:py-[30px] md:py-[24px]'>
            <div className='mx-auto flex w-full flex-col justify-center sm:w-[640px] rounded-[14px] bg-[#F1F1F1] px-6 md:px-8 md:gap-8 gap-6 py-10'>
                <div className='text-center page-title-hero text-[#121212]'>パスワード設定</div>
                {
                    isConfirmOk ?
                        (
                            <div className='flex flex-col gap-8 w-full'>
                                <div className='md:p-4 sm:p-2 p-1 flex flex-col items-center md:gap-6 gap-4 rounded-[4px] text-[1.0625rem] font-medium w-full'>
                                    <img className='h-[100px]' src={SuccessIcon} alt='email-icon' />
                                    <div className='text-center'>
                                        パスワードの設定が完了しました。
                                    </div>
                                </div>

                                <Button
                                    asChild
                                    type='button'
                                    variant='default'
                                    className='w-full rounded-[10px] h-14 text-base xsm:text-lg font-bold mb-10'
                                >
                                    <Link to={ROUTES.home.href}>トップページへ</Link>
                                </Button>
                            </div>
                        ) :
                        (
                            <div className='space-x-6 md:space-x-8'>
                                <Form {...form}>
                                    <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6 md:space-y-8'>
                                        <div className='space-y-4 sm:space-y-6'>
                                            <div className='text-center text-base text-[#016743] font-medium'>新しいパスワードを入力してください</div>
                                            <FormField
                                                control={form.control}
                                                name='password'
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel className='text-base text-[#121212] font-medium' required>
                                                            新しいパスワード
                                                        </FormLabel>
                                                        <FormControl>
                                                            <PasswordInput
                                                                type={!showNewPassword ? 'password' : 'text'}
                                                                autoComplete='off'
                                                                onToggleIcon={() => setShowNewPassword(!showNewPassword)}
                                                                className='rounded-none h-14 bg-white'
                                                                {...field}
                                                                onChange={(value) => {
                                                                    field.onChange(value);
                                                                    form.trigger(['password']);
                                                                    if (form.getValues('passwordConfirm')?.length > 0) {
                                                                        form.trigger('passwordConfirm');
                                                                    }
                                                                }}
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={form.control}
                                                name='passwordConfirm'
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel className='text-base text-[#121212] font-medium' required>
                                                            新しいパスワードの確認
                                                        </FormLabel>
                                                        <FormControl>
                                                            <PasswordInput
                                                                type={!showConfirmNewPassword ? 'password' : 'text'}
                                                                autoComplete='false'
                                                                className='rounded-none h-14 bg-white'
                                                                onPaste={e => e.preventDefault()}
                                                                onToggleIcon={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                                                                {...field}
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>

                                        <Button
                                            type='submit'
                                            variant='default'
                                            className='w-full rounded-[10px] h-12 md:h-[56px] text-sm xsm:text-lg font-bold'
                                        >
                                            送信
                                        </Button>

                                        <div className='h-[1px] w-full bg-[#D9D9D9]' />

                                        <p className='text-center'>
                                            <Link to={ROUTES.home.href} className='inline-flex text-base items-center gap-1.5 font-medium group'>
                                                <ArrowLeft size={24} color='#343330' strokeWidth={1.25} className='transition-transform duration-500 group-hover:-translate-x-1' />
                                                ログインに戻る
                                            </Link>
                                        </p>
                                    </form>
                                </Form>
                            </div>
                        )
                }
            </div>
        </div>

    );
});
